/**
 * EditPersonForm — inline add/edit form for a Person row on /p/:placeId.
 * Per UX §3.13 + AC-060.
 */
import * as React from 'react';
import type { PlaceId } from '@shared/types';
import { Button } from './ui/Button';
import { TextInput } from './ui/TextInput';
import { TextArea } from './ui/TextArea';
import { FormField } from './ui/FormField';
import { apiFetch } from '../lib/api';
import { useToast } from '../lib/store';
import { useErrorToasts } from '../lib/errorToast';

interface PersonInput {
  name: string;
  role?: string;
  notes?: string;
}

export interface EditPersonFormProps {
  placeId: PlaceId;
  initial?: { id: string; name: string; role: string | null; notes: string | null };
  onSuccess?: () => void;
  onCancel?: () => void;
}

export function EditPersonForm({
  placeId,
  initial,
  onSuccess,
  onCancel,
}: EditPersonFormProps) {
  const [name, setName] = React.useState(initial?.name ?? '');
  const [role, setRole] = React.useState(initial?.role ?? '');
  const [notes, setNotes] = React.useState(initial?.notes ?? '');
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState('');
  const toast = useToast();
  const errors = useErrorToasts();

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!name.trim()) {
      setError('Give them a name.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const body: PersonInput = { name: name.trim() };
      if (role.trim()) body.role = role.trim();
      if (notes.trim()) body.notes = notes.trim();
      if (initial) {
        await apiFetch(`/people/${initial.id}`, {
          method: 'PATCH',
          body,
        });
        toast.success('Updated.');
      } else {
        await apiFetch(`/places/${placeId}/people`, {
          method: 'POST',
          body,
        });
        toast.success('Person added.');
      }
      onSuccess?.();
    } catch (err) {
      if (!errors.handle(err)) {
        setError(err instanceof Error ? err.message : "We couldn't save.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form
      onSubmit={onSubmit}
      noValidate
      className="space-y-4 py-4 border-b border-ink-100"
    >
      <FormField label="Name" htmlFor="ep-name" {...(error ? { error } : {})}>
        <TextInput
          id="ep-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Their name"
          autoFocus
          required
        />
      </FormField>
      <FormField label="Role" optional htmlFor="ep-role">
        <TextInput
          id="ep-role"
          value={role}
          onChange={(e) => setRole(e.target.value)}
          placeholder="Concierge, sommelier, owner"
        />
      </FormField>
      <FormField label="Notes" optional htmlFor="ep-notes">
        <TextArea
          id="ep-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          minRows={2}
          placeholder="Anything worth remembering."
        />
      </FormField>
      <div className="flex items-center justify-end gap-3">
        {onCancel ? (
          <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
            Cancel
          </Button>
        ) : null}
        <Button
          type="submit"
          variant="primary"
          size="sm"
          loading={submitting}
          loadingLabel="Saving…"
        >
          {initial ? 'Save' : 'Add'}
        </Button>
      </div>
    </form>
  );
}
